import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Container,
  Dropdown,
  Form,
  Header,
  Label,
  Menu,
  Message,
  Segment,
  TextArea,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { marked } from 'marked';

const RESOLUTION_OPTIONS = [
  { key: '720P', value: '720P', text: '720P' },
  { key: '1080P', value: '1080P', text: '1080P' },
];

const IMAGE_SIZE_OPTIONS = [
  { key: '1024*1024', value: '1024*1024', text: '1024×1024' },
  { key: '720*1280',  value: '720*1280',  text: '720×1280 (竖)' },
  { key: '1280*720',  value: '1280*720',  text: '1280×720 (横)' },
  { key: '1280*1280', value: '1280*1280', text: '1280×1280' },
];

const STATUS_COLORS = {
  PENDING: 'yellow',
  RUNNING: 'blue',
  SUCCEEDED: 'green',
  FAILED: 'red',
  CANCELED: 'orange',
};

const POLL_INTERVAL = 3000;
const LS_KEY = 'video_task_id';

const ChatTest = () => {
  const [searchParams] = useSearchParams();
  const initialModel = searchParams.get('model') || '';

  const status = JSON.parse(localStorage.getItem('status') || '{}');
  const apiBase = (
    status.api_address ||
    status.server_address ||
    window.location.origin
  ).replace(/\/$/, '') + '/v1';

  // Shared
  const [token, setToken] = useState('');
  const [models, setModels] = useState(
    initialModel
      ? [{ key: initialModel, value: initialModel, text: initialModel }]
      : []
  );
  const [model, setModel] = useState(initialModel);
  const [activeTab, setActiveTab] = useState('text');

  // Text tab
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Video tab
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoResolution, setVideoResolution] = useState('1080P');
  const [videoTaskId, setVideoTaskId] = useState(() => localStorage.getItem(LS_KEY) || '');
  const [videoStatus, setVideoStatus] = useState('');
  const [videoURL, setVideoURL] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState('');
  const pollRef = useRef(null);

  // Wan-image tab
  const [imgPrompt, setImgPrompt] = useState('');
  const [imgSize, setImgSize] = useState('1024*1024');
  const [imgN, setImgN] = useState(1);
  const [imgResults, setImgResults] = useState([]);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState('');

  const fetchModels = async (t) => {
    if (!t) return;
    try {
      const res = await fetch(`${apiBase}/models`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      if (data.data) {
        const opts = data.data.map((m) => ({ key: m.id, value: m.id, text: m.id }));
        const merged = opts.some((o) => o.value === initialModel)
          ? opts
          : initialModel
          ? [{ key: initialModel, value: initialModel, text: initialModel }, ...opts]
          : opts;
        setModels(merged);
        if (!model && merged.length > 0) setModel(merged[0].value);
      }
    } catch {
      // Silently ignore
    }
  };

  // --- Text tab ---
  const handleSend = async () => {
    if (!token) { setError('请填写 API Token'); return; }
    if (!model) { setError('请选择或输入模型名称'); return; }
    if (!input.trim()) { setError('请输入内容'); return; }

    setError('');
    setOutput('');
    setLoading(true);

    try {
      const res = await fetch(`${apiBase}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model,
          messages: [{ role: 'user', content: input.trim() }],
          stream: true,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setError(errData?.error?.message || `请求失败: ${res.status}`);
        setLoading(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const d = line.slice(6).trim();
          if (d === '[DONE]') continue;
          try {
            const json = JSON.parse(d);
            const delta = json.choices?.[0]?.delta?.content;
            if (delta) {
              full += delta;
              setOutput(full);
            }
          } catch {
            // skip malformed SSE chunks
          }
        }
      }
    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  const handleAddModel = (_, { value }) => {
    setModels((prev) => [...prev, { key: value, value, text: value }]);
    setModel(value);
  };

  // --- Video tab ---
  const pollTask = useCallback(async (taskId, tok) => {
    try {
      const res = await fetch(`${apiBase}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      const data = await res.json();
      if (data.error) {
        setVideoError(data.error.message || '轮询失败');
        setVideoStatus('FAILED');
        localStorage.removeItem(LS_KEY);
        return true; // stop polling
      }
      const st = data.output?.task_status || '';
      setVideoStatus(st);
      if (st === 'SUCCEEDED') {
        setVideoURL(data.output?.video_url || '');
        localStorage.removeItem(LS_KEY);
        return true;
      }
      if (st === 'FAILED' || st === 'CANCELED') {
        setVideoError(data.message || `任务${st === 'FAILED' ? '失败' : '已取消'}`);
        localStorage.removeItem(LS_KEY);
        return true;
      }
      return false;
    } catch (e) {
      setVideoError(e.message);
      return true;
    }
  }, [apiBase]);

  const startPolling = useCallback((taskId, tok) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const done = await pollTask(taskId, tok);
      if (done && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, POLL_INTERVAL);
  }, [pollTask]);

  // Resume polling on mount if there's a stored task
  useEffect(() => {
    if (videoTaskId && token) {
      setVideoStatus('POLLING');
      pollTask(videoTaskId, token).then((done) => {
        if (!done) startPolling(videoTaskId, token);
      });
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVideoSubmit = async () => {
    if (!token) { setVideoError('请填写 API Token'); return; }
    if (!model) { setVideoError('请选择或输入模型名称'); return; }
    if (!videoPrompt.trim()) { setVideoError('请输入提示词'); return; }

    setVideoError('');
    setVideoURL('');
    setVideoStatus('');
    setVideoLoading(true);

    try {
      const res = await fetch(`${apiBase}/wan/video/generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          model,
          input: {
            prompt: videoPrompt.trim(),
          },
          parameters: {
            duration: videoDuration,
            resolution: videoResolution,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setVideoError(data?.error?.message || data?.message || `请求失败: ${res.status}`);
        setVideoLoading(false);
        return;
      }

      const taskId = data.output?.task_id;
      if (!taskId) {
        setVideoError('未返回 task_id');
        setVideoLoading(false);
        return;
      }

      setVideoTaskId(taskId);
      setVideoStatus('PENDING');
      localStorage.setItem(LS_KEY, taskId);
      startPolling(taskId, token);
    } catch (e) {
      setVideoError(e.message);
    }

    setVideoLoading(false);
  };

  const handleVideoReset = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setVideoTaskId('');
    setVideoStatus('');
    setVideoURL('');
    setVideoError('');
    localStorage.removeItem(LS_KEY);
  };

  // --- Wan-image tab ---

  const handleImgSubmit = async () => {
    if (!token) { setImgError('请填写 API Token'); return; }
    if (!model) { setImgError('请选择或输入模型名称'); return; }
    if (!imgPrompt.trim()) { setImgError('请输入提示词'); return; }

    setImgError('');
    setImgResults([]);
    setImgLoading(true);

    const body = {
      model,
      input: {
        messages: [{ role: 'user', content: [{ text: imgPrompt.trim() }]}],
      },
      parameters: {
        size: imgSize,
        n: imgN,
      },
    };

    try {
      const res = await fetch(`${apiBase}/wan/images/generations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setImgError(data?.error?.message || data?.message || `请求失败: ${res.status}`);
        setImgLoading(false);
        return;
      }

      // Sync response: output.choices[].message.content[].image
      const urls = (data.output?.choices || [])
        .flatMap((c) => c.message?.content || [])
        .filter((item) => item.type === 'image' && item.image)
        .map((item) => item.image);

      if (urls.length === 0) {
        setImgError('响应中未找到图像 URL');
      } else {
        setImgResults(urls);
      }
    } catch (e) {
      setImgError(e.message);
    }

    setImgLoading(false);
  };

  return (
    <Container style={{ marginTop: '2em', maxWidth: '800px' }}>
      <Header as='h2'>API 可用性测试</Header>
      <Message info>
        简单的可用性测试，不会保留对话信息。
      </Message>

      <Form>
        <Form.Field>
          <label>
            API Token
            <span style={{ fontWeight: 'normal', marginLeft: '8px', color: '#888', fontSize: '0.9em' }}>
              （在 <Link to='/token'>令牌页面</Link> 创建或查看你的 token）
            </span>
          </label>
          <Form.Input
            placeholder='sk-morouter-...'
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onBlur={() => fetchModels(token)}
            type='password'
          />
        </Form.Field>
        <Form.Field>
          <label>模型</label>
          <Dropdown
            selection
            search
            allowAdditions
            options={models}
            value={model}
            onChange={(_, { value }) => setModel(value)}
            onAddItem={handleAddModel}
            placeholder='选择模型或手动输入'
            fluid
          />
        </Form.Field>
      </Form>

      <Menu pointing secondary style={{ marginTop: '1.5em' }}>
        <Menu.Item active={activeTab === 'text'} onClick={() => setActiveTab('text')}>
          文本测试
        </Menu.Item>
        {/* <Menu.Item active={activeTab === 'video'} onClick={() => setActiveTab('video')}> */}
        {/*   视频测试 */}
        {/* </Menu.Item> */}
        <Menu.Item active={activeTab === 'image'} onClick={() => setActiveTab('image')}>
          图像测试
        </Menu.Item>
      </Menu>

      {activeTab === 'text' && (
        <Form error={!!error}>
          <Form.Field>
            <label>消息</label>
            <TextArea
              placeholder='在这里输入你的消息...'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={4}
            />
          </Form.Field>
          {error && <Message error content={error} />}
          <Button primary onClick={handleSend} loading={loading} disabled={loading}>
            发送
          </Button>
          {output && (
            <div style={{ marginTop: '2em' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5em' }}>输出</label>
              <Segment>
                <div
                  dangerouslySetInnerHTML={{ __html: marked(output) }}
                  style={{ fontFamily: 'inherit' }}
                />
              </Segment>
            </div>
          )}
        </Form>
      )}

      {activeTab === 'video' && (
        <Form error={!!videoError}>
          <Form.Field>
            <label>提示词</label>
            <TextArea
              placeholder='描述你想生成的视频内容...'
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              rows={3}
            />
          </Form.Field>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>时长（秒）</label>
              <Form.Input
                type='number'
                min={1}
                value={videoDuration}
                onChange={(e) => setVideoDuration(parseInt(e.target.value) || 5)}
              />
            </Form.Field>
            <Form.Field>
              <label>分辨率</label>
              <Dropdown
                selection
                options={RESOLUTION_OPTIONS}
                value={videoResolution}
                onChange={(_, { value }) => setVideoResolution(value)}
                fluid
              />
            </Form.Field>
          </Form.Group>

          {videoError && <Message error content={videoError} />}

          {!videoTaskId && (
            <Button primary onClick={handleVideoSubmit} loading={videoLoading} disabled={videoLoading}>
              提交生成
            </Button>
          )}

          {videoTaskId && (
            <Segment>
              <p>
                Task ID: <code>{videoTaskId}</code>
                {videoStatus && (
                  <Label color={STATUS_COLORS[videoStatus] || 'grey'} size='small' style={{ marginLeft: 8 }}>
                    {videoStatus}
                  </Label>
                )}
              </p>
              {videoURL && (
                <div style={{ marginTop: '1em' }}>
                  <video src={videoURL} controls style={{ maxWidth: '100%' }} />
                  <p style={{ marginTop: '0.5em' }}>
                    <a href={videoURL} target='_blank' rel='noreferrer'>下载视频</a>
                  </p>
                </div>
              )}
              <Button size='small' onClick={handleVideoReset} style={{ marginTop: '0.5em' }}>
                重置
              </Button>
            </Segment>
          )}
        </Form>
      )}

      {activeTab === 'image' && (
        <Form error={!!imgError}>
          <Form.Field>
            <label>提示词</label>
            <TextArea
              placeholder='描述你想生成的图像内容...'
              value={imgPrompt}
              onChange={(e) => setImgPrompt(e.target.value)}
              rows={3}
            />
          </Form.Field>
          <Form.Group widths='equal'>
            <Form.Field>
              <label>尺寸</label>
              <Dropdown
                selection
                options={IMAGE_SIZE_OPTIONS}
                value={imgSize}
                onChange={(_, { value }) => setImgSize(value)}
                fluid
              />
            </Form.Field>
            <Form.Field>
              <label>数量</label>
              <Form.Input
                type='number'
                min={1}
                max={4}
                value={imgN}
                onChange={(e) => setImgN(parseInt(e.target.value) || 1)}
              />
            </Form.Field>
          </Form.Group>

          {imgError && <Message error content={imgError} />}

          <Button primary onClick={handleImgSubmit} loading={imgLoading} disabled={imgLoading}>
            生成图像
          </Button>

          {imgResults.length > 0 && (
            <div style={{ marginTop: '1em', display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
              {imgResults.map((url, i) => (
                <div key={i}>
                  <img src={url} alt={`生成图像 ${i + 1}`} style={{ maxWidth: '100%', maxHeight: '400px' }} />
                  <p style={{ marginTop: '0.25em' }}>
                    <a href={url} target='_blank' rel='noreferrer'>下载图像 {i + 1}</a>
                  </p>
                </div>
              ))}
            </div>
          )}
        </Form>
      )}
    </Container>
  );
};

export default ChatTest;
